'use client';

import { IcCloseCircle, IconButton, TextField, Typography } from '@musinsa/mds';
import type React from 'react';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

interface ClearableTextFieldProps {
  title?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  errorText?: string;
  showClearButton?: boolean;
  containerClassName?: string;
  titleClassName?: string;
  className?: string;
  name?: string;
  disabled?: boolean;
  maxLength?: number;
  inputMode?: 'text' | 'numeric';
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
  pattern?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const ClearableTextField = forwardRef<HTMLInputElement, ClearableTextFieldProps>(
  (
    {
      title,
      placeholder,
      value = '',
      onChange,
      error = false,
      errorText,
      showClearButton = true,
      containerClassName = '',
      titleClassName = '',
      className,
      name,
      disabled,
      maxLength,
      inputMode,
      enterKeyHint,
      pattern,
      onKeyDown,
    },
    ref,
  ) => {
    const hasValue = value && value.length > 0;

    const handleClear = () => {
      onChange?.({
        target: {
          value: '',
        },
      } as React.ChangeEvent<HTMLInputElement>);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.currentTarget.blur();
      }
      onKeyDown?.(e);
    };

    const showTitle = Boolean(title);

    return (
      <div className={twMerge('flex flex-col gap-2 py-2', containerClassName)}>
        {showTitle && (
          <Typography size="14" weight="medium" className={titleClassName}>
            {title}
          </Typography>
        )}

        <div className="inline-flex relative w-full">
          <TextField
            ref={ref}
            fullWidth
            variant="outlined"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            error={error}
            className={className}
            name={name}
            disabled={disabled}
            maxLength={maxLength}
            inputMode={inputMode}
            enterKeyHint={enterKeyHint || 'done'}
            pattern={pattern}
          />
          {showClearButton && !disabled && hasValue && (
            <IconButton
              className="absolute right-2 top-2"
              size="size20x20"
              onClick={handleClear}
              type="button"
            >
              <IcCloseCircle fillColor="gray-400" />
            </IconButton>
          )}
        </div>

        {error && errorText && (
          <Typography variant="textEtc11pxReg" colorScheme="red">
            {errorText}
          </Typography>
        )}
      </div>
    );
  },
);

ClearableTextField.displayName = 'ClearableTextField';
